import { ResponseBackendErrorContentType, ResponseErrorContentType, ResponseErrorType } from '@/types/sharedTypes';
import camelCase from 'lodash/camelCase';
import { TEXT_HTML_CONTENT_TYPE, UNEXPECTED_RESPONSE_ERROR_TYPE } from '@/api/const';
import { ResponseError } from '@/bundle/Error/classes/ResponseError';
import { isString } from '@/helpers/objectHelpers';

export type FormattedResponseType<T> = {
  body?: T;
  headers?: {
    [key: string]: string;
  };
  error?: ResponseErrorType;
};

export type ResponseType<T> = {
  json?: T;
  response?: Response;
};

const getHeaders = (response: Response, headers: string[] = []) => {
  return headers.reduce((result, header) => {
    const headerValue = response?.headers.get(header);
    const headerName = camelCase(header);

    result[headerName] = headerValue;

    return result;
  }, {});
};

const getErrorDetails = <T>(response: ResponseType<T>) => {
  const responseContentType = response.response.headers?.get('Content-Type');
  const errorDetails = response.json as ResponseBackendErrorContentType;

  // Note: Unexpected error happened on backend if we get "text/html" content type - fallback to default error
  const isTextHTMLContentType = responseContentType === TEXT_HTML_CONTENT_TYPE;
  const errorContent: ResponseErrorContentType = isTextHTMLContentType
    ? new ResponseError().message
    : errorDetails?.error_message || errorDetails?.error_details;
  const errorCode = isTextHTMLContentType ? null : errorDetails?.error_code;
  const errorType = isTextHTMLContentType ? UNEXPECTED_RESPONSE_ERROR_TYPE : errorDetails?.error_type;

  return {
    errorContent,
    errorCode,
    errorType,
  };
};

export const prepareResponse = <T>(response: ResponseType<T>, headers: string[] = []): FormattedResponseType<T> => {
  const headersData = getHeaders(response?.response, headers);

  if (!response?.response.ok) {
    const errorDetails = getErrorDetails(response);

    return {
      body: null,
      error: {
        ...errorDetails,
        statusCode: response.response.status,
      },
      headers: { ...headersData },
    };
  }

  return { body: response?.json, error: null, headers: { ...headersData } };
};

export const throwResponseError = (error: Error) => {
  // Rethrow original errors, since we handle them separately in <ErrorBoundary> component
  throw error;
};

export const getResponseError = (error: ResponseErrorType, errorKey?: string): string => {
  if (!error) return;

  const errorContent = error.errorContent;

  // Regular error
  if (isString(errorContent)) {
    return errorContent;
  }

  // Validation error by key
  const errorByKey = errorContent?.[errorKey];

  if (Array.isArray(errorByKey)) {
    return errorByKey[0];
  }

  if (errorByKey) {
    return errorByKey;
  }

  // Validation error
  if (errorContent && typeof errorContent === 'object') {
    const hasValidationErrors = Object.values(errorContent).some((value) => Array.isArray(value) && value.length > 0);

    if (hasValidationErrors) {
      return 'Validation failed. Please review your input data.';
    }
  }
};

export const getExcludedResponseError = (error: ResponseErrorType, excludeError: string) => {
  const formError = getResponseError(error);

  if (formError === excludeError) {
    return '';
  }

  return formError;
};

export const hasResponseError = (apiError: ResponseErrorType, matchError: string) => {
  const formError = getResponseError(apiError);

  return formError === matchError;
};

export const withPreparedResponse = async <T>(call: () => Promise<{ response?: Response; json?: T }>) => {
  try {
    return prepareResponse(await call());
  } catch (e) {
    throwResponseError(e);
  }
};
